/**
 * Store-wide $100 order minimum (theme-level).
 *
 * WHAT THIS COVERS: the cart page checkout button, the cart notification popup's
 * checkout button, the cart drawer's button if the theme is ever switched to
 * drawer mode, and the express/dynamic checkout buttons (Shop Pay, PayPal,
 * Apple Pay, Google Pay) rendered in the cart.
 *
 * WHAT THIS CANNOT COVER: this is browser JavaScript, so it is a deterrent, not
 * enforcement. Anyone who navigates straight to /checkout, checks out from a
 * product page's express button, or uses the Storefront API will not be stopped.
 * Enforcing server-side requires a Shopify Function, which needs either Shopify
 * Plus (custom app) or a public App Store app. See the note in
 * shopify-apps/medtech-wholesale-rules/HANDOFF.md.
 *
 * Coexists with practi-dose-moq.js via the shared MTOrderGate registry below --
 * each rule registers a reason, and checkout is blocked if ANY reason is active.
 * Never toggle the checkout button directly from a rule script, or two rules
 * will fight and the last one to run wins.
 */
(function () {
  var MINIMUM_CENTS = 10000;
  var WARNING_ID = 'mt-order-minimum-warning';

  var CHECKOUT_SELECTOR = [
    '#checkout',
    '#CartDrawer-Checkout',
    'button[name="checkout"]',
    'input[name="checkout"]',
  ].join(', ');

  var EXPRESS_SELECTOR = [
    '.cart__dynamic-checkout-buttons',
    '.additional-checkout-buttons',
    '#dynamic-checkout-cart',
  ].join(', ');

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2);
  }

  /**
   * Shared registry so multiple minimum rules can coexist. Each rule owns a key
   * and reports whether it is currently blocking; the gate applies the union.
   */
  var gate = (window.MTOrderGate = window.MTOrderGate || {
    reasons: {},

    set: function (key, blocked, message) {
      if (blocked) {
        this.reasons[key] = message || '';
      } else {
        delete this.reasons[key];
      }
      this.apply();
    },

    isBlocked: function () {
      return Object.keys(this.reasons).length > 0;
    },

    messages: function () {
      var self = this;
      return Object.keys(this.reasons)
        .map(function (key) {
          return self.reasons[key];
        })
        .filter(Boolean);
    },

    // Reentrancy guard. apply() writes to the DOM, and the MutationObserver
    // below calls apply() when the DOM changes -- without this the two feed each
    // other and lock the browser up. Do not remove.
    applying: false,

    apply: function () {
      if (this.applying) return;
      this.applying = true;

      if (this.pauseObserver) this.pauseObserver();
      try {
        this.applyNow();
      } finally {
        this.applying = false;
        if (this.resumeObserver) this.resumeObserver();
      }
    },

    applyNow: function () {
      var blocked = this.isBlocked();

      var buttons = document.querySelectorAll(CHECKOUT_SELECTOR);
      for (var i = 0; i < buttons.length; i++) {
        var button = buttons[i];
        // Don't fight the theme's own empty-cart disabling.
        if (!blocked && button.getAttribute('data-mt-gate') !== 'blocked') continue;

        button.disabled = blocked;
        button.setAttribute('data-mt-gate', blocked ? 'blocked' : 'open');
        button.style.opacity = blocked ? '0.5' : '';
        button.style.cursor = blocked ? 'not-allowed' : '';
      }

      var express = document.querySelectorAll(EXPRESS_SELECTOR);
      for (var j = 0; j < express.length; j++) {
        express[j].style.display = blocked ? 'none' : '';
      }

      this.renderMessage(blocked ? this.messages() : []);
    },

    renderMessage: function (messages) {
      var existing = document.getElementById(WARNING_ID);

      if (!messages.length) {
        if (existing) existing.style.display = 'none';
        return;
      }

      if (!existing) {
        var anchor = document.querySelector('.cart__ctas') || document.querySelector('[name="checkout"]');
        if (!anchor) return;

        existing = document.createElement('div');
        existing.id = WARNING_ID;
        existing.setAttribute('role', 'status');
        existing.style.cssText =
          'background:#fff8e1;border:1px solid #f0c040;border-radius:4px;' +
          'padding:10px 14px;margin-bottom:12px;font-size:0.95em;color:#7a5c00;';
        (anchor.parentNode || anchor).insertBefore(existing, anchor);
      }

      // Only write if the text actually changed. Rewriting identical text is
      // still a DOM mutation, which is what fed the observer loop.
      var text = '⚠️ ' + messages.join(' ');
      if (existing.textContent !== text) existing.textContent = text;
      if (existing.style.display !== 'block') existing.style.display = 'block';
    },
  });

  /** The store-wide rule: cart total (after discounts, before shipping/tax). */
  function checkMinimum() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (response) {
        return response.json();
      })
      .then(function (cart) {
        // An empty cart is the theme's problem, not ours.
        if (!cart.items || cart.items.length === 0) {
          gate.set('order-minimum', false);
          return;
        }

        var short = MINIMUM_CENTS - cart.total_price;
        if (short > 0) {
          gate.set(
            'order-minimum',
            true,
            'Wholesale orders have a ' +
              formatMoney(MINIMUM_CENTS) +
              ' minimum before shipping. Please add ' +
              formatMoney(short) +
              ' more to continue.'
          );
        } else {
          gate.set('order-minimum', false);
        }
      })
      .catch(function () {
        // Fail open: a failed cart fetch must not strand a legitimate order.
        gate.set('order-minimum', false);
      });
  }

  // Backstop for anything that slips past the disabled attribute -- a stale
  // button, a theme re-render mid-click, or an app-injected checkout control.
  function intercept(event) {
    if (!gate.isBlocked()) return;

    var target = event.target.closest
      ? event.target.closest(CHECKOUT_SELECTOR)
      : null;
    if (!target) return;

    event.preventDefault();
    event.stopPropagation();
    gate.apply();
  }

  document.addEventListener('click', intercept, true);
  document.addEventListener('submit', function (event) {
    if (!gate.isBlocked()) return;
    var form = event.target;
    if (form && form.querySelector && form.querySelector(CHECKOUT_SELECTOR)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, true);

  // Re-check whenever the cart changes. Dawn re-renders cart sections after its
  // own fetches rather than firing a single documented event, so patch fetch.
  var nativeFetch = window.fetch;
  window.fetch = function () {
    var url = arguments[0];
    var path = typeof url === 'string' ? url : (url && url.url) || '';
    var result = nativeFetch.apply(this, arguments);

    if (path.indexOf('/cart') !== -1 && path.indexOf('/cart.js') === -1) {
      result.then(function () {
        // One cart-change signal for EVERY rule (2026-09-05 audit SF-04). Dawn's cart.js
        // publishes through its private pubsub, not a DOM event, so practi-dose-moq.js —
        // which only listens for DOM `cart:updated` — went stale on quantity changes while
        // this rule refreshed itself. Dispatching the DOM event here re-checks this rule
        // (listener below) AND the Practi-Dose minimum from a single observer.
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { source: 'order-minimum' } }));
      }).catch(function () {});
    }

    return result;
  };

  // Re-apply after the theme swaps cart markup, which drops our attributes.
  // Coalesced into one call per frame, and paused while we are the ones writing,
  // so observing our own writes can never turn into a loop.
  var observing = false;
  var queued = false;

  var observer = new MutationObserver(function () {
    if (queued) return;
    queued = true;
    requestAnimationFrame(function () {
      queued = false;
      gate.apply();
    });
  });

  gate.pauseObserver = function () {
    if (!observing) return;
    observer.disconnect();
    observing = false;
  };

  gate.resumeObserver = function () {
    if (observing || !document.body) return;
    observer.observe(document.body, { childList: true, subtree: true });
    observing = true;
  };

  function start() {
    checkMinimum();
    gate.resumeObserver();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('cart:updated', checkMinimum);
  document.addEventListener('cart:refresh', checkMinimum);
})();
